import { useState, useMemo, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Container,
  Box,
  Title,
  Text,
  Stack,
  Modal,
  Button,
  Group,
  Badge,
  Select,
  MultiSelect,
  Paper,
  TextInput,
  Table,
  Tabs,
  Pagination,
  Loader,
  Center,
  ScrollArea,
} from "@mantine/core";
import { useDisclosure, useDebouncedCallback } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconFilter,
  IconRefresh,
  IconBook2,
  IconClock,
  IconFileDescription,
  IconSend,
} from "@tabler/icons-react";
import {
  useDiagnosisSearch,
  useCreateDiagnosis,
  useUpdateDiagnosis,
  useToggleDiagnosisActive,
  useSetDiagnosisLinkedStains,
} from "../../../hooks/admin/useDiagnosis";
import { useDiagnosisCategoriesAdmin } from "../../../hooks/admin/useDiagnosisCategories";
import { useCptCodesAdmin } from "../../../hooks/admin/useCptCodes";
import { useIcdCodesAdmin } from "../../../hooks/admin/useIcdCodes";
import {
  usePendingMacroRequests,
  useMyMacroRequests,
} from "../../../hooks/admin/useMacroRequests";
import DiagnosisForm, {
  DiagnosisFormValues,
} from "../../../components/admin/diagnosis/DiagnosisForm";
import RequestMacroModal from "../../../components/admin/diagnosis/RequestMacroModal";
import PendingMacroRequests from "../../../components/admin/diagnosis/PendingMacroRequests";
import MyMacroRequests from "../../../components/admin/diagnosis/MyMacroRequests";
import DiagnosisMacroRow from "../../../components/admin/diagnosis/DiagnosisMacroRow";
import {
  Diagnosis,
  DiagnosisSearchParams,
} from "../../../services/admin/diagnosis.service";
import { useAuth } from "../../../hooks/useAuth";
import { useHasPermission } from "../../../hooks/useHasPermission";

interface DiagnosisFilters {
  categoryIds?: string[];
  cptIds?: string[];
  icdIds?: string[];
  status?: "active" | "inactive";
}

const ITEMS_PER_PAGE = 20;

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export default function DiagnosisAdminPage() {
  const { user } = useAuth();

  // Permission-based access control (more robust than role name checking)
  const canEdit = useHasPermission("reference_data.manage");
  const canRequestMacro = useHasPermission("macro_library.request");
  const isAdmin = canEdit; // Admins can edit, so they have reference_data.manage

  // URL-tracked state: tab, search, filters, and pagination
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") || "macros";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const urlSearch = searchParams.get("search") || "";
  const expandedRow = searchParams.get("expanded");
  const filters = useMemo<DiagnosisFilters>(
    () => ({
      categoryIds:
        searchParams.get("categoryIds")?.split(",").filter(Boolean) ||
        undefined,
      cptIds:
        searchParams.get("cptIds")?.split(",").filter(Boolean) || undefined,
      icdIds:
        searchParams.get("icdIds")?.split(",").filter(Boolean) || undefined,
      status:
        searchParams.get("status") === "active"
          ? "active"
          : searchParams.get("status") === "inactive"
          ? "inactive"
          : undefined,
    }),
    [searchParams]
  );

  const updateParams = useCallback(
    (updates: Record<string, string | null>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(updates).forEach(([k, v]) => {
            if (v === null || v === "") next.delete(k);
            else next.set(k, v);
          });
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleTabChange = useCallback(
    (tab: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tab && tab !== "macros") next.set("tab", tab);
          else next.delete("tab");
          next.delete("page");
          next.delete("expanded");
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // Redirect away from tabs the current user cannot access
  useEffect(() => {
    if (!user) return;
    if (
      (activeTab === "requests" && !isAdmin) ||
      (activeTab === "my-requests" && !canRequestMacro)
    ) {
      setSearchParams({}, { replace: true });
    }
  }, [user, activeTab, isAdmin, canRequestMacro, setSearchParams]);

  // Modal states
  const [formModalOpened, { open: openFormModal, close: closeFormModal }] =
    useDisclosure(false);
  const [
    requestModalOpened,
    { open: openRequestModal, close: closeRequestModal },
  ] = useDisclosure(false);
  const [
    filterModalOpened,
    { open: openFilterModal, close: closeFilterModal },
  ] = useDisclosure(false);
  const [
    confirmModalOpened,
    { open: openConfirmModal, close: closeConfirmModal },
  ] = useDisclosure(false);

  // Page state
  const [editing, setEditing] = useState<Diagnosis | null>(null);
  const [togglingItem, setTogglingItem] = useState<Diagnosis | null>(null);
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || ""
  );
  const [pendingFilters, setPendingFilters] = useState<DiagnosisFilters>({});

  // Debounced sync of the search input into the URL
  const applySearchToUrl = useDebouncedCallback((value: string) => {
    updateParams({ search: value || null, page: null, expanded: null });
  }, 300);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchInput(event.currentTarget.value);
      applySearchToUrl(event.currentTarget.value);
    },
    [applySearchToUrl]
  );

  // Build search params for backend
  const diagnosisSearchParams: DiagnosisSearchParams = useMemo(() => {
    const params: DiagnosisSearchParams = {
      page,
      limit: ITEMS_PER_PAGE,
    };
    if (urlSearch) params.search = urlSearch;
    if (filters.categoryIds?.length) params.categoryIds = filters.categoryIds;
    if (filters.cptIds?.length) params.cptIds = filters.cptIds;
    if (filters.icdIds?.length) params.icdIds = filters.icdIds;
    if (filters.status) params.status = filters.status;
    return params;
  }, [urlSearch, filters, page]);

  // Data hooks - backend filtering and pagination
  const {
    data: diagnosisResult,
    isLoading,
    refetch,
  } = useDiagnosisSearch(diagnosisSearchParams);
  const { data: categories = [] } = useDiagnosisCategoriesAdmin();
  const { data: cptCodes = [] } = useCptCodesAdmin();
  const { data: icdCodes = [] } = useIcdCodesAdmin();
  const { data: pendingRequests = [] } = usePendingMacroRequests(isAdmin);
  const { data: myRequests = [] } = useMyMacroRequests(canRequestMacro);
  const createDiagnosis = useCreateDiagnosis();
  const updateDiagnosis = useUpdateDiagnosis();
  const toggleActive = useToggleDiagnosisActive();
  const setLinkedStains = useSetDiagnosisLinkedStains();

  // Extract data from paginated result
  const diagnoses = useMemo(
    () => diagnosisResult?.data || [],
    [diagnosisResult]
  );
  const total = diagnosisResult?.total || 0;
  const totalPages = diagnosisResult?.totalPages || 1;

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        filters.categoryIds?.length ||
          filters.cptIds?.length ||
          filters.icdIds?.length ||
          filters.status
      ),
    [filters]
  );

  // Handlers
  const handleAdd = useCallback(() => {
    setEditing(null);
    openFormModal();
  }, [openFormModal]);

  const handleEdit = useCallback(
    (item: Diagnosis) => {
      setEditing(item);
      openFormModal();
    },
    [openFormModal]
  );

  const handleSubmit = useCallback(
    async (values: DiagnosisFormValues) => {
      const { linkedStainIds, ...rest } = values;
      const payload = {
        ...rest,
        createdById: values.scope === "personal" ? user?.id || null : null,
      };

      let diagnosisId: string;
      if (editing) {
        const updated = await updateDiagnosis.mutateAsync({
          id: editing.id,
          ...payload,
        });
        diagnosisId = updated.id;
      } else {
        const created = await createDiagnosis.mutateAsync(payload);
        diagnosisId = created.id;
      }

      // Save linked stains separately (idempotent replace)
      await setLinkedStains.mutateAsync({
        diagnosisId,
        stainIds: linkedStainIds,
      });

      closeFormModal();
    },
    [
      editing,
      user?.id,
      updateDiagnosis,
      createDiagnosis,
      setLinkedStains,
      closeFormModal,
    ]
  );

  const handleToggleActive = useCallback(
    (item: Diagnosis) => {
      setTogglingItem(item);
      openConfirmModal();
    },
    [openConfirmModal]
  );

  const confirmToggleActive = useCallback(async () => {
    if (!togglingItem) return;
    await toggleActive.mutateAsync({
      id: togglingItem.id,
      isActive: !togglingItem.isActive,
    });
    closeConfirmModal();
    setTogglingItem(null);
  }, [togglingItem, toggleActive, closeConfirmModal]);

  const handleCloseConfirmModal = useCallback(() => {
    closeConfirmModal();
    setTogglingItem(null);
  }, [closeConfirmModal]);

  const handleClearAllFilters = useCallback(() => {
    setPendingFilters({});
    setSearchInput("");
    updateParams({
      search: null,
      categoryIds: null,
      cptIds: null,
      icdIds: null,
      status: null,
      page: null,
      expanded: null,
    });
  }, [updateParams]);

  const handleOpenFilterModal = useCallback(() => {
    setPendingFilters({ ...filters });
    openFilterModal();
  }, [filters, openFilterModal]);

  const handleApplyFilters = useCallback(() => {
    updateParams({
      categoryIds: pendingFilters.categoryIds?.length
        ? pendingFilters.categoryIds.join(",")
        : null,
      cptIds: pendingFilters.cptIds?.length
        ? pendingFilters.cptIds.join(",")
        : null,
      icdIds: pendingFilters.icdIds?.length
        ? pendingFilters.icdIds.join(",")
        : null,
      status: pendingFilters.status || null,
      page: null,
      expanded: null,
    });
    closeFilterModal();
  }, [pendingFilters, updateParams, closeFilterModal]);

  const handleClearPendingFilters = useCallback(() => {
    setPendingFilters({});
  }, []);

  const handlePendingCategoriesChange = useCallback((values: string[]) => {
    setPendingFilters((prev) => ({
      ...prev,
      categoryIds: values.length > 0 ? values : undefined,
    }));
  }, []);

  const handlePendingCptChange = useCallback((values: string[]) => {
    setPendingFilters((prev) => ({
      ...prev,
      cptIds: values.length > 0 ? values : undefined,
    }));
  }, []);

  const handlePendingIcdChange = useCallback((values: string[]) => {
    setPendingFilters((prev) => ({
      ...prev,
      icdIds: values.length > 0 ? values : undefined,
    }));
  }, []);

  const handlePendingStatusChange = useCallback((value: string | null) => {
    setPendingFilters((prev) => ({
      ...prev,
      status: (value as "active" | "inactive" | null) || undefined,
    }));
  }, []);

  const handleToggleRow = useCallback(
    (id: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (next.get("expanded") === id) next.delete("expanded");
          else next.set("expanded", id);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  const handlePageChange = useCallback(
    (nextPage: number) => {
      updateParams({
        page: nextPage === 1 ? null : String(nextPage),
        expanded: null,
      });
    },
    [updateParams]
  );

  // Options for dropdowns
  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
      })),
    [categories]
  );

  const cptCodeOptions = useMemo(
    () =>
      cptCodes.map((code) => ({
        value: code.id,
        label: `${code.code} - ${code.description}`,
      })),
    [cptCodes]
  );

  const icdCodeOptions = useMemo(
    () =>
      icdCodes.map((code) => ({
        value: code.id,
        label: `${code.code} - ${code.description}`,
      })),
    [icdCodes]
  );

  return (
    <Container fluid py="xl">
      <Box style={{ maxWidth: "1600px", margin: "0 auto" }}>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <div style={{ flex: 1, minWidth: 0 }}>
              <Group gap="sm">
                <IconBook2 size={28} color="var(--mantine-color-blue-6)" />
                <Title order={2}>Diagnosis Macro Library</Title>
              </Group>
              <Text size="sm" c="dimmed" mt="xs">
                Manage diagnosis macros with rich text templates, variables,
                categories, CPT codes, and ICD codes. Pathologists use these
                codes for quick diagnosis entry.
              </Text>
            </div>
            <Group gap="sm">
              {canRequestMacro && (
                <Button
                  variant="light"
                  leftSection={<IconSend size={16} />}
                  onClick={openRequestModal}
                >
                  Request New Macro
                </Button>
              )}
              {canEdit && (
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={handleAdd}
                >
                  Add New Macro
                </Button>
              )}
            </Group>
          </Group>

          {/* Tabs: Macro Library / Pending Requests / My Requests */}
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            keepMounted={false}
          >
            <Tabs.List>
              <Tabs.Tab value="macros" leftSection={<IconBook2 size={16} />}>
                Macro Library
              </Tabs.Tab>
              {isAdmin && (
                <Tabs.Tab
                  value="requests"
                  leftSection={<IconClock size={16} />}
                  rightSection={
                    pendingRequests.length > 0 ? (
                      <Badge size="sm" variant="filled" color="orange">
                        {pendingRequests.length}
                      </Badge>
                    ) : undefined
                  }
                >
                  Pending Requests
                </Tabs.Tab>
              )}
              {canRequestMacro && (
                <Tabs.Tab
                  value="my-requests"
                  leftSection={<IconFileDescription size={16} />}
                  rightSection={
                    myRequests.length > 0 ? (
                      <Badge size="sm" variant="light" color="violet">
                        {myRequests.length}
                      </Badge>
                    ) : undefined
                  }
                >
                  My Requests
                </Tabs.Tab>
              )}
            </Tabs.List>

            {/* ===== MACRO LIBRARY TAB ===== */}
            <Tabs.Panel value="macros" pt="md">
              <Stack gap="lg">
                {/* Search and Filters Row */}
                <Group align="stretch" grow>
                  {/* Search Section */}
                  <Paper p="md" withBorder style={{ flex: 2 }}>
                    <Stack gap="sm">
                      <Group>
                        <IconSearch size={20} />
                        <Text fw={500}>Search</Text>
                      </Group>
                      <TextInput
                        placeholder="Search by code, description, category, CPT, or ICD..."
                        value={searchInput}
                        onChange={handleSearchChange}
                        leftSection={<IconSearch size={16} />}
                      />
                    </Stack>
                  </Paper>

                  {/* Filters Section */}
                  <Paper p="md" withBorder style={{ flex: 1 }}>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Group>
                          <IconFilter size={20} />
                          <Text fw={500}>Filters</Text>
                        </Group>
                        <Button
                          variant="subtle"
                          size="compact-sm"
                          onClick={handleRefresh}
                          loading={isLoading}
                          leftSection={<IconRefresh size={16} />}
                        />
                      </Group>
                      <Button
                        variant="light"
                        onClick={handleOpenFilterModal}
                        fullWidth
                      >
                        {hasActiveFilters ? "Edit Filters" : "Add Filters"}
                      </Button>
                      {hasActiveFilters && (
                        <Group gap="xs" wrap="wrap">
                          <Text size="sm" c="dimmed">
                            Active:
                          </Text>
                          {filters.categoryIds &&
                            filters.categoryIds.length > 0 && (
                              <Badge variant="light" color="blue">
                                {filters.categoryIds.length} Categor
                                {filters.categoryIds.length > 1 ? "ies" : "y"}
                              </Badge>
                            )}
                          {filters.cptIds && filters.cptIds.length > 0 && (
                            <Badge variant="light" color="teal">
                              {filters.cptIds.length} CPT
                            </Badge>
                          )}
                          {filters.icdIds && filters.icdIds.length > 0 && (
                            <Badge variant="light" color="orange">
                              {filters.icdIds.length} ICD
                            </Badge>
                          )}
                          {filters.status && (
                            <Badge
                              variant="light"
                              color={
                                filters.status === "active" ? "green" : "red"
                              }
                            >
                              {filters.status === "active"
                                ? "Active"
                                : "Inactive"}
                            </Badge>
                          )}
                          <Button
                            variant="subtle"
                            size="compact-xs"
                            color="gray"
                            onClick={handleClearAllFilters}
                          >
                            Clear All
                          </Button>
                        </Group>
                      )}
                      <Text size="sm" c="dimmed">
                        {total.toLocaleString()} macro
                        {total !== 1 ? "s" : ""} found
                      </Text>
                    </Stack>
                  </Paper>
                </Group>

                {/* Table Section */}
                <Paper p="md" withBorder>
                  <Stack gap="md">
                    {/* Table Header with Pagination */}
                    <Group justify="space-between">
                      <Text fw={500} size="lg">
                        Diagnosis Macros ({total.toLocaleString()})
                      </Text>
                      {totalPages > 1 && (
                        <Group gap="md">
                          <Text size="sm" c="dimmed">
                            Showing{" "}
                            {((page - 1) * ITEMS_PER_PAGE + 1).toLocaleString()}
                            -
                            {Math.min(
                              page * ITEMS_PER_PAGE,
                              total
                            ).toLocaleString()}{" "}
                            of {total.toLocaleString()}
                          </Text>
                          <Pagination
                            total={totalPages}
                            value={page}
                            onChange={handlePageChange}
                            size="sm"
                          />
                        </Group>
                      )}
                    </Group>

                    {/* Table */}
                    {isLoading ? (
                      <Center p="xl">
                        <Loader />
                      </Center>
                    ) : diagnoses.length === 0 ? (
                      <Text c="dimmed" ta="center" py="xl">
                        {hasActiveFilters || urlSearch
                          ? "No macros match the current filters"
                          : "No diagnosis macros found"}
                      </Text>
                    ) : (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 40 }} />
                            <Table.Th style={{ width: 120 }}>Code</Table.Th>
                            <Table.Th style={{ width: 120 }}>Category</Table.Th>
                            <Table.Th style={{ width: 100 }}>CPT</Table.Th>
                            <Table.Th style={{ width: 100 }}>ICD</Table.Th>
                            <Table.Th>Diagnosis Description</Table.Th>
                            <Table.Th style={{ width: 140 }}>Scope</Table.Th>
                            <Table.Th style={{ width: 100 }}>Status</Table.Th>
                            {canEdit && (
                              <Table.Th style={{ width: 100 }}>
                                Actions
                              </Table.Th>
                            )}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {diagnoses.map((item) => (
                            <DiagnosisMacroRow
                              key={item.id}
                              item={item}
                              canEdit={canEdit}
                              isExpanded={expandedRow === item.id}
                              onToggleExpand={handleToggleRow}
                              onEdit={handleEdit}
                              onToggleActive={handleToggleActive}
                            />
                          ))}
                        </Table.Tbody>
                      </Table>
                    )}

                    {/* Bottom Pagination for long lists */}
                    {totalPages > 1 && diagnoses.length > 10 && (
                      <Group justify="center" mt="md">
                        <Pagination
                          total={totalPages}
                          value={page}
                          onChange={handlePageChange}
                          size="sm"
                        />
                      </Group>
                    )}
                  </Stack>
                </Paper>
              </Stack>
            </Tabs.Panel>

            {/* ===== PENDING REQUESTS TAB (admin only) ===== */}
            {isAdmin && (
              <Tabs.Panel value="requests" pt="md">
                <PendingMacroRequests />
              </Tabs.Panel>
            )}

            {/* ===== MY REQUESTS TAB (pathologist only) ===== */}
            {canRequestMacro && (
              <Tabs.Panel value="my-requests" pt="md">
                <MyMacroRequests />
              </Tabs.Panel>
            )}
          </Tabs>
        </Stack>

        {/* Request Macro Modal */}
        <RequestMacroModal
          opened={requestModalOpened}
          onClose={closeRequestModal}
          categories={categoryOptions}
          cptCodes={cptCodeOptions}
          icdCodes={icdCodeOptions}
        />

        {/* Create/Edit Modal - XL size for rich text editors (admin only) */}
        {canEdit && (
          <Modal
            opened={formModalOpened}
            onClose={closeFormModal}
            title={editing ? "Edit Diagnosis Macro" : "Add New Diagnosis Macro"}
            size="xl"
            closeOnClickOutside={false}
            centered
            styles={{ body: { padding: 0 } }}
          >
            <ScrollArea.Autosize
              mah="calc(100vh - 300px)"
              offsetScrollbars
              px="md"
              pt="md"
            >
              <DiagnosisForm
                initialValues={editing || undefined}
                onSubmit={handleSubmit}
                categories={categoryOptions}
                cptCodes={cptCodeOptions}
                icdCodes={icdCodeOptions}
                formId="diagnosis-admin-form"
              />
            </ScrollArea.Autosize>

            <Box
              px="md"
              py="sm"
              style={{
                borderTop: "1px solid var(--mantine-color-default-border)",
              }}
            >
              <Group justify="space-between">
                <Button variant="default" onClick={closeFormModal}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={
                    createDiagnosis.isPending ||
                    updateDiagnosis.isPending ||
                    setLinkedStains.isPending
                  }
                  form="diagnosis-admin-form"
                >
                  {editing ? "Update" : "Create"}
                </Button>
              </Group>
            </Box>
          </Modal>
        )}

        {/* Filter Modal */}
        <Modal
          opened={filterModalOpened}
          onClose={closeFilterModal}
          title="Filter Diagnosis Macros"
          size="md"
          closeOnClickOutside={false}
        >
          <Stack gap="md">
            <MultiSelect
              label="Categories"
              placeholder="Select categories..."
              data={categoryOptions}
              value={pendingFilters.categoryIds || []}
              onChange={handlePendingCategoriesChange}
              searchable
              clearable
            />

            <MultiSelect
              label="CPT Codes"
              placeholder="Select CPT codes..."
              data={cptCodeOptions}
              value={pendingFilters.cptIds || []}
              onChange={handlePendingCptChange}
              searchable
              clearable
            />

            <MultiSelect
              label="ICD Codes"
              placeholder="Select ICD codes..."
              data={icdCodeOptions}
              value={pendingFilters.icdIds || []}
              onChange={handlePendingIcdChange}
              searchable
              clearable
            />

            <Select
              label="Status"
              placeholder="All statuses"
              data={STATUS_OPTIONS}
              value={pendingFilters.status || null}
              onChange={handlePendingStatusChange}
              clearable
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={handleClearPendingFilters}>
                Clear All
              </Button>
              <Button onClick={handleApplyFilters}>Apply Filters</Button>
            </Group>
          </Stack>
        </Modal>

        {/* Confirmation Modal for Activate/Deactivate (admin only) */}
        {canEdit && (
          <Modal
            opened={confirmModalOpened}
            onClose={handleCloseConfirmModal}
            title={
              togglingItem?.isActive
                ? "Deactivate Diagnosis Macro"
                : "Activate Diagnosis Macro"
            }
            size="sm"
            centered
          >
            <Stack gap="md">
              <Text>
                Are you sure you want to{" "}
                <Text span fw={600}>
                  {togglingItem?.isActive ? "deactivate" : "activate"}
                </Text>{" "}
                the diagnosis macro{" "}
                <Text span fw={600}>
                  &quot;{togglingItem?.code}&quot;
                </Text>
                ?
              </Text>
              {togglingItem?.isActive && (
                <Text size="sm" c="dimmed">
                  Deactivating this macro will hide it from the
                  pathologist&apos;s diagnosis entry dropdown.
                </Text>
              )}
              <Group justify="flex-end" mt="md">
                <Button variant="subtle" onClick={handleCloseConfirmModal}>
                  Cancel
                </Button>
                <Button
                  color={togglingItem?.isActive ? "red" : "green"}
                  loading={toggleActive.isPending}
                  onClick={confirmToggleActive}
                >
                  {togglingItem?.isActive ? "Deactivate" : "Activate"}
                </Button>
              </Group>
            </Stack>
          </Modal>
        )}
      </Box>
    </Container>
  );
}
